<script setup lang="ts">
/**
 * 变量操作编辑器
 *
 * 复用于数值/字符串变量事件的属性编辑，支持直接绑定 EventVar 或通过 v-model 受控。
 */
import { computed, ref, watch } from 'vue'
import AppSelectField from '@/components/AppSelectField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import { NUMERIC_TARGET_MODE_OPTIONS } from '@/components/eventEditors/eventSelectOptions'
import VariableIndexField from '@/components/eventEditors/VariableIndexField.vue'
import DialogTextEditor from '@/components/eventEditors/DialogTextEditor.vue'
import { useSceneEditorStore } from '@/store/sceneEditor'
import type EventVar from '@/model/EventClass/EventVar'
import type EventVarsEx from '@/model/EventClass/EventVarsEx'
import type EventString from '@/model/EventClass/EventString'
import { VarType } from '@/model/EventClass/EventVar'
import type { VariableIndexScope } from '@/features/event/variableIndex'
import {
  applyNumericOperationForm,
  readNumericOperationForm,
  VARIABLE_OPERATOR_OPTIONS,
  VARIABLE_TIME_PART_OPTIONS,
  VARIABLE_VALUE_TYPE_OPTIONS,
  cloneNumericOperationForm,
  cloneStringOperationForm,
  type NumericOperationForm,
  type StringOperationForm,
} from '@/features/event/variableBatch'

// props
const props = defineProps<{
  mode: 'numeric' | 'string'
  modelValue?: NumericOperationForm | StringOperationForm
  event?: EventVar | EventVarsEx | EventString
  indexLabel?: string
  indexValue?: number | string
  indexPickerScope?: VariableIndexScope
  indexReadonly?: boolean
  captionTitle?: string
  captionSubtitle?: string
  previewText?: string
  previewPlaceholder?: string
  helpLabel?: string
  helpText?: string
  variableLabelRevision?: number
  indexDisplayLabel?: string
}>()

// emits
const emit = defineEmits<{
  'update:modelValue': [value: NumericOperationForm | StringOperationForm]
  'update:indexValue': [value: number]
}>()

// store
const editor = useSceneEditorStore()

// state
const eventRenderVersion = ref(0)

// computed
const numericEvent = computed(() => props.mode === 'numeric' ? props.event as EventVar | EventVarsEx | undefined : undefined)
const stringEvent = computed(() => props.mode === 'string' ? props.event as EventString | undefined : undefined)
const showNumericTargetMode = computed(() => props.mode === 'numeric' && !!numericEvent.value)
const numericValue = computed(() => {
  eventRenderVersion.value
  if (props.mode === 'numeric' && numericEvent.value) {
    return readNumericOperationForm(numericEvent.value)
  }
  return (props.modelValue as NumericOperationForm) ?? cloneNumericOperationForm({
    isIndex: false,
    operator: 0,
    type: VarType.Constant,
    constantValue: 0,
    otherVarIndex: 0,
    randomMin: 0,
    randomMax: 100,
    varExIndex: 0,
    indexVar: 0,
    timePart: 0,
    isResultMinus: false,
  })
})
const stringValue = computed(() => {
  eventRenderVersion.value
  if (props.mode === 'string' && stringEvent.value) {
    return cloneStringOperationForm({
      isVarIndex: stringEvent.value.IsVarIndex,
      content: stringEvent.value.Content,
    })
  }
  return (props.modelValue as StringOperationForm) ?? cloneStringOperationForm({
    isVarIndex: false,
    content: '',
  })
})
const showResultMinus = computed(() => props.mode === 'numeric' && numericValue.value.type === VarType.ServerTime)
const resolvedIndexPickerScope = computed<VariableIndexScope | undefined>(() => {
  if (props.mode === 'numeric' && numericEvent.value) {
    return numericValue.value.isIndex ? 'var' : props.indexPickerScope
  }
  return props.indexPickerScope
})
const resolvedIndexTitle = computed(() => {
  if (props.mode === 'numeric' && numericEvent.value) {
    return numericValue.value.isIndex ? '选择数值' : `选择${props.indexLabel ?? '变量'}`
  }
  return `选择${props.indexLabel ?? '变量'}`
})

// watch
watch(
  () => props.event,
  () => {
    eventRenderVersion.value += 1
  },
)

// handlers
function patchNumeric(partial: Partial<NumericOperationForm>) {
  const next = cloneNumericOperationForm(numericValue.value)
  Object.assign(next, partial)
  if (next.type !== VarType.ServerTime) {
    next.isResultMinus = false
  }
  if (numericEvent.value) {
    applyNumericOperationForm(numericEvent.value, next)
    editor.notifySceneEventValueChanged()
    eventRenderVersion.value += 1
    return
  }
  emit('update:modelValue', next)
}

function patchString(partial: Partial<StringOperationForm>) {
  const next = cloneStringOperationForm(stringValue.value)
  Object.assign(next, partial)
  if (stringEvent.value) {
    stringEvent.value.IsVarIndex = next.isVarIndex
    stringEvent.value.Content = next.content
    editor.notifySceneEventValueChanged()
    eventRenderVersion.value += 1
    return
  }
  emit('update:modelValue', next)
}

function patchIndex(value: number) {
  if (props.event && 'Index' in props.event) {
    ;(props.event as EventVar | EventVarsEx | EventString).Index = value
    editor.notifySceneEventValueChanged()
  }
  emit('update:indexValue', value)
  eventRenderVersion.value += 1
}
</script>

<template>
  <div class="variable-operation-editor">
    <div v-if="captionTitle || captionSubtitle" class="variable-operation-editor__caption">
      <div v-if="captionTitle" class="variable-operation-editor__caption-main">{{ captionTitle }}</div>
      <div v-if="captionSubtitle" class="variable-operation-editor__caption-sub">{{ captionSubtitle }}</div>
    </div>

    <label v-if="!showNumericTargetMode && indexLabel" class="variable-row">
      <span class="variable-row__label">{{ indexLabel }}</span>
      <VariableIndexField
        v-if="resolvedIndexPickerScope"
        class="variable-input--md"
        :model-value="Number(indexValue ?? 0)"
        :scope="resolvedIndexPickerScope"
        :readonly="indexReadonly"
        :disabled="indexReadonly"
        :title="resolvedIndexTitle"
        :revision="variableLabelRevision"
        :display-label="indexDisplayLabel"
        @update:model-value="patchIndex"
      />
      <input
        v-else
        class="variable-input variable-input--md"
        type="number"
        :readonly="indexReadonly"
        :value="indexValue ?? ''"
        @input="patchIndex(Number(($event.target as HTMLInputElement).value || '0'))"
      />
    </label>

    <template v-if="mode === 'numeric'">
      <label v-if="showNumericTargetMode" class="variable-row">
        <span class="variable-row__label">数值类型</span>
        <AppSelectField
          input-class="variable-input"
          :model-value="numericValue.isIndex ? 'index' : 'direct'"
          :options="NUMERIC_TARGET_MODE_OPTIONS"
          @update:model-value="patchNumeric({ isIndex: $event === 'index' })"
        />
      </label>

      <label v-if="showNumericTargetMode && indexLabel" class="variable-row">
        <span class="variable-row__label">数值</span>
        <VariableIndexField
          v-if="resolvedIndexPickerScope"
          class="variable-input--md"
          :model-value="Number(indexValue ?? 0)"
          :scope="resolvedIndexPickerScope"
          :readonly="indexReadonly"
          :disabled="indexReadonly"
          :title="resolvedIndexTitle"
          :revision="variableLabelRevision"
          :display-label="indexDisplayLabel"
          @update:model-value="patchIndex"
        />
        <input
          v-else
          class="variable-input variable-input--md"
          type="number"
          :readonly="indexReadonly"
          :value="indexValue ?? ''"
          @input="patchIndex(Number(($event.target as HTMLInputElement).value || '0'))"
        />
      </label>

      <label class="variable-row">
        <span class="variable-row__label">操作符</span>
        <AppSelectField
          input-class="variable-input"
          :model-value="numericValue.operator"
          :options="VARIABLE_OPERATOR_OPTIONS"
          @update:model-value="patchNumeric({ operator: Number($event) })"
        />
      </label>

      <label class="variable-row">
        <span class="variable-row__label">值类型</span>
        <AppSelectField
          input-class="variable-input"
          :model-value="numericValue.type"
          :options="VARIABLE_VALUE_TYPE_OPTIONS"
          @update:model-value="patchNumeric({ type: Number($event) as NumericOperationForm['type'] })"
        />
      </label>

      <label v-if="numericValue.type === VarType.Constant" class="variable-row">
        <span class="variable-row__label">常量值</span>
        <NumberStepperField
          class="variable-input--sm"
          :model-value="numericValue.constantValue"
          @update:model-value="patchNumeric({ constantValue: $event })"
        />
      </label>

      <label v-else-if="numericValue.type === VarType.Other" class="variable-row">
        <span class="variable-row__label">数值索引</span>
        <VariableIndexField
          class="variable-input--sm"
          :model-value="numericValue.otherVarIndex"
          scope="var"
          title="选择数值索引"
          @update:model-value="patchNumeric({ otherVarIndex: $event })"
        />
      </label>

      <template v-else-if="numericValue.type === VarType.Random">
        <label class="variable-row">
          <span class="variable-row__label">随机最小值</span>
          <NumberStepperField
            class="variable-input--sm"
            :model-value="numericValue.randomMin"
            @update:model-value="patchNumeric({ randomMin: $event })"
          />
        </label>
        <label class="variable-row">
          <span class="variable-row__label">随机最大值</span>
          <NumberStepperField
            class="variable-input--sm"
            :model-value="numericValue.randomMax"
            @update:model-value="patchNumeric({ randomMax: $event })"
          />
        </label>
      </template>

      <label v-else-if="numericValue.type === VarType.VarEx" class="variable-row">
        <span class="variable-row__label">二周目索引</span>
        <VariableIndexField
          class="variable-input--sm"
          :model-value="numericValue.varExIndex"
          scope="varEx"
          title="选择二周目索引"
          @update:model-value="patchNumeric({ varExIndex: $event })"
        />
      </label>

      <label v-else-if="numericValue.type === VarType.Index" class="variable-row">
        <span class="variable-row__label">索引变量</span>
        <VariableIndexField
          class="variable-input--sm"
          :model-value="numericValue.indexVar"
          scope="var"
          title="选择索引变量"
          @update:model-value="patchNumeric({ indexVar: $event })"
        />
      </label>

      <label
        v-else-if="numericValue.type === VarType.ServerTime || numericValue.type === VarType.LocalTime"
        class="variable-row"
      >
        <span class="variable-row__label">时间字段</span>
        <AppSelectField
          input-class="variable-input"
          :model-value="numericValue.timePart"
          :options="VARIABLE_TIME_PART_OPTIONS"
          @update:model-value="patchNumeric({ timePart: Number($event) })"
        />
      </label>

      <label v-if="showResultMinus" class="variable-row">
        <span class="variable-row__label">失败返回 -1</span>
        <span class="variable-check-wrap">
          <input
            class="variable-check"
            type="checkbox"
            :checked="numericValue.isResultMinus"
            @change.stop="patchNumeric({ isResultMinus: ($event.target as HTMLInputElement).checked })"
          />
        </span>
      </label>
    </template>

    <template v-else>
      <label class="variable-row variable-row--start">
        <span class="variable-row__label">字符串内容</span>
        <DialogTextEditor
          class="variable-input variable-input--textarea"
          :rows="6"
          :model-value="stringValue.content"
          @update:model-value="patchString({ content: $event })"
        />
      </label>
    </template>

    <div v-if="previewText || previewPlaceholder" class="variable-operation-editor__preview">
      {{ previewText || previewPlaceholder }}
    </div>

    <div v-if="helpText" class="variable-help-row">
      <div v-if="helpLabel" class="variable-help-row__label">{{ helpLabel }}</div>
      <div class="variable-operation-editor__help" :class="{ 'variable-operation-editor__help--full': !helpLabel }">
        {{ helpText }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.variable-operation-editor {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.variable-operation-editor__caption {
  padding: 0 0 8px;
  border-bottom: 1px solid var(--theme-border);
}

.variable-operation-editor__caption-main {
  color: var(--theme-text-heading);
  font-size: 12px;
}

.variable-operation-editor__caption-sub {
  color: var(--theme-text-secondary);
  font-size: 11px;
}

.variable-operation-editor__preview {
  padding: 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-secondary);
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
}

.variable-operation-editor__help {
  padding: 8px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-secondary);
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
}

.variable-operation-editor__help--full {
  width: 100%;
}

.variable-help-row {
  display: grid;
  grid-template-columns: var(--event-prop-label-w, 86px) minmax(0, 1fr);
  align-items: stretch;
  gap: 10px;
  padding: 8px 10px;
  box-sizing: border-box;
}

.variable-help-row__label {
  color: var(--theme-text-muted);
  font-size: 13px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: 2px;
}

.variable-row {
  display: grid;
  grid-template-columns: var(--event-prop-label-w, 86px) minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  min-height: 36px;
  box-sizing: border-box;
}

.variable-row--start {
  align-items: start;
}

.variable-row__label {
  color: var(--theme-text-muted);
  font-size: 13px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.variable-input {
  width: 100%;
  min-width: 0;
  padding: 4px 8px;
  border: 1px solid var(--theme-bg-secondary);
  background: var(--theme-bg-surface);
  color: var(--theme-text-heading);
  font-size: 13px;
  box-sizing: border-box;
}

.variable-input--sm {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  --number-stepper-sm-min-width: var(--number-stepper-min-width);
}

.variable-input--md {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  --number-stepper-md-min-width: var(--number-stepper-min-width);
}

.variable-input--textarea {
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
}

.variable-input:focus {
  outline: none;
  border-color: var(--theme-accent);
}

.variable-check-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-width: 0;
}

.variable-check {
  margin: 0;
}
</style>

<style>
.event-prop-form > .variable-operation-editor > .prop-row:nth-child(odd),
.event-prop-form > .variable-operation-editor > .variable-row:nth-child(odd),
.event-prop-form > .variable-operation-editor > .variable-help-row:nth-child(odd) {
  background: var(--theme-prop-row-a);
}

.event-prop-form > .variable-operation-editor > .prop-row:nth-child(even),
.event-prop-form > .variable-operation-editor > .variable-row:nth-child(even),
.event-prop-form > .variable-operation-editor > .variable-help-row:nth-child(even) {
  background: var(--theme-prop-row-b);
}
</style>
